import { Ref } from "react";
import { TaskType } from "../../../types/TaskType";
import ViewerFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragmentConfig";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import TitledTextFieldViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/TitledTextFieldViewerFragment/TitledTextFieldViewerFragment";
import CompletableChecklistViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/CompletableChecklistViewerFragment/CompletableChecklistViewerFragment";
import TitledBoxedTextViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/TitledBoxedTextViewerFragment/TitledBoxedTextViewerFragment";
import UserAndTeamViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserAndTeamViewerFragment/UserAndTeamViewerFragment";
import LocationViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/LocationViewerFragment/LocationViewerFragment";
import TitledTextAreaViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/TitledTextAreaViewerFragment/TitledTextAreaViewerFragment";
import MediaLocationViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/MediaLocationViewerFragment/MediaLocationViewerFragment";
import InvalidViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/InvalidViewerFragment/InvalidViewerFragment";
import FailedCriteriaViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/FailedCriteriaViewerFragment/FailedCriteriaViewerFragment";
import { Loading } from "@coworker/apprestructured/src/task-management/loading";
import { ArticlesOrderedDetailsViewerFragment } from "./ArticlesOrderedDetailsViewerFragment/ArticlesOrderedDetailsViewerFragment";
import RefillQuantityViewerFragment from "./RefillQuantityViewerFragment/RefillQuantityViewerFragment";
import MediasQuantityViewerFragment from "./MediasQuantityViewerFragment/MediasQuantityViewerFragment";
import MediasPrintPtagsViewerFragment from "./MediasPrintPtagsViewerFragment/MediasPrintPtagsViewerFragment";
import MediaLocationsViewerFragment from "./MediaLocationsViewerFragment/MediaLocationsViewerFragment";
import ProgressViewerFragment from "./ProgressViewerFragment/ProgressViewerFragment";
import TitledArticleViewerFragment from "./TitledArticleViewerFragment/TitledArticleViewerFragment";
import TestbuyViewerFragment from "./TestbuyViewerFragment/TestbuyViewerFragment";
import TabTitleContentViewerFragment from "./TabTitleContentViewerFragment/TabTitleContentViewerFragment";
import TitledTextWithBadgesViewerFragment from "./TitledTextWithBadgesViewerFragment/TitledTextWithBadgesViewerFragment";
import GroupedViewerFragment from "./GroupedViewerFragment/GroupedViewerFragment";

export type ViewerFragmentHandle = {
  valid: boolean;
  canEdit?: boolean;
};

export type ViewerFragmentCommonProps = {
  // Block all user interaction with this fragment, usually while making api requests.
  blockInput: boolean;
  // The task being viewed, which is the original task merged with all pending changes.
  task: Record<string, unknown> | Loading;
  // Callback for making changes to the task that is being viewed.
  // Note that this doesn't trigger any api request. It just adds a part to the PatchTask
  // that is sent when the user explicitly saves the task later on.
  onTaskChange: (part: Record<string, unknown>) => void;
  // Whether to show validation errors. Toggled when the user tries to complete a task
  // with invalid data, mirroring the same pattern used by the creator fragments.
  showValidation: boolean;
  taskType: TaskType | Loading;
};

type ViewerFragmentProps = {
  config: ViewerFragmentConfig;
  commonProps: ViewerFragmentCommonProps;
  ref?: Ref<ViewerFragmentHandle>;
};

function ViewerFragment({ config, commonProps, ref }: ViewerFragmentProps) {
  const { id, props: configProps } = config;
  switch (id) {
    case "titled-boxed-text-viewer-fragment": {
      return (
        <TitledBoxedTextViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "titled-text-field-viewer-fragment": {
      return (
        <TitledTextFieldViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "completable-checklist-viewer-fragment": {
      return (
        <CompletableChecklistViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "user-and-team-viewer-fragment": {
      return (
        <UserAndTeamViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "location-viewer-fragment": {
      return (
        <LocationViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "media-location-viewer-fragment": {
      return (
        <MediaLocationViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "titled-text-area-viewer-fragment": {
      return (
        <TitledTextAreaViewerFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "articles-quantity-viewer-fragment": {
      return (
        <ArticlesOrderedDetailsViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "refill-quantity-viewer-fragment": {
      return (
        <RefillQuantityViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "medias-quantity-viewer-fragment": {
      return (
        <MediasQuantityViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "medias-print-ptags-viewer-fragment": {
      return (
        <MediasPrintPtagsViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "failed-criteria-viewer-fragment": {
      return (
        <FailedCriteriaViewerFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "media-locations-viewer-fragment": {
      return (
        <MediaLocationsViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "titled-article-viewer-fragment": {
      return (
        <TitledArticleViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "progress-viewer-fragment": {
      return (
        <ProgressViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "tab-title-content-viewer-fragment": {
      return (
        <TabTitleContentViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "testbuy-viewer-fragment": {
      return (
        <TestbuyViewerFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "titled-text-with-badges-viewer-fragment": {
      return (
        <TitledTextWithBadgesViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "grouped-viewer-fragment": {
      return (
        <GroupedViewerFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    default: {
      console.error(`'${id}' does not map to any viewer fragment component`);
      reportMessageToSentry(
        `Viewer fragment config id does not map to any viewer fragment component`,
        {
          id,
        },
        "error"
      );
      return <InvalidViewerFragment />;
    }
  }
}

export default ViewerFragment;
